import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, FormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LocationSettingsFormFactory } from '../../../models/location-settings-form.factory';
import { LocationServiceDialogComponent } from './location-service-dialog.component';
import { LocationServiceBulkAddDialogComponent } from './location-service-bulk-add-dialog.component';
import { LocationServiceDeleteDialogComponent } from './location-service-delete-dialog.component';
import {
  LocationConceptService,
  LocationConceptAdminDto,
  ConceptStatusDto
} from '../../../services/location-concept.service';
import { MasterService } from '../../../../../master/services/master.service';
import { MasterModel } from '../../../../../master/models/master.model';

@Component({
    selector: 'app-location-service-section',
    templateUrl: './location-service-section.component.html',
    styleUrls: ['./location-service-section.component.css'],
    standalone: false
})
export class LocationServiceSectionComponent implements OnInit, OnDestroy {
  @Input() formGroup!: UntypedFormGroup;
  @Input() locationId: string | null = null;

  availableConcepts: MasterModel[] = [];
  availableStaff: MasterModel[] = [];
  conceptStatuses: ConceptStatusDto[] = [];

  concepts: LocationConceptAdminDto[] = [];
  totalItems = 0;

  searchTerm = '';
  filterStatus: number | null = null;

  isLoading = false;
  isLoadingStatuses = false;

  errorMessage: string | null = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private conceptService: LocationConceptService,
    private masterService: MasterService
  ) {}

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.loadConceptStatuses();
    this.loadCatalogs();
    this.loadConcepts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.loadConcepts();
      });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  private loadConceptStatuses(): void {
    this.isLoadingStatuses = true;
    this.conceptService.getConceptStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isSuccess && response.values) {
            this.conceptStatuses = response.values;
          }
          this.isLoadingStatuses = false;
        },
        error: (err) => {
          console.error('Error loading concept statuses:', err);
          this.isLoadingStatuses = false;
        }
      });
  }

  private loadCatalogs(): void {
    forkJoin({
      concepts: this.masterService.getConcepts(),
      employees: this.masterService.getEmployees()
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: { concepts: MasterModel[], employees: MasterModel[] }) => {
        this.availableConcepts = result.concepts;
        this.availableStaff = result.employees;
      },
      error: (err) => {
        console.error('Error loading catalogs from master service:', err);
        this.availableConcepts = [];
        this.availableStaff = [];
      }
    });
  }

  loadConcepts(): void {
    if (!this.locationId) {
      console.warn('No locationId available');
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    this.conceptService.searchLocationConcepts(
      this.locationId,
      this.searchTerm || undefined,
      this.filterStatus !== null ? this.filterStatus : undefined
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response) => {
        this.isLoading = false;
        
        if (response.isSuccess && response.value) {
          this.concepts = response.value.concepts || [];
          this.totalItems = response.value.totalCount || 0;
        } else if (response.error) {
          this.errorMessage = this.getErrorMessage(response.error);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading concepts:', err);
        this.errorMessage = 'Error al cargar los conceptos. Por favor, inténtalo de nuevo.';
      }
    });
  }

  onStatusFilterChange(statusId: number | null): void {
    this.filterStatus = statusId;
    this.loadConcepts();
  }

  getStatusName(statusId: number): string {
    const status = this.conceptStatuses.find(s => s.statusMerchantConceptId === statusId);
    return status ? status.name : 'Desconocido';
  }

  openServiceDialog(concept?: LocationConceptAdminDto): void {
    if (!this.locationId) {
      console.error('No se puede abrir el diálogo: locationId no disponible');
      this.errorMessage = 'No se puede abrir el diálogo. ID del centro de trabajo no disponible.';
      return;
    }

    const dialogRef = this.dialog.open(LocationServiceDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        concept: concept || null,
        locationId: this.locationId,
        availableConcepts: this.availableConcepts,
        availableStaff: this.availableStaff,
        conceptStatuses: this.conceptStatuses
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadConcepts();
      }
    });
  }

  openBulkAddDialog(): void {
    if (!this.locationId) {
      console.error('No se puede abrir el diálogo bulk: locationId no disponible');
      this.errorMessage = 'No se puede abrir el diálogo. ID del centro de trabajo  no disponible.';
      return;
    }

    const existingConceptIds = this.concepts
      .map(c => c.conceptId)
      .filter(id => id); 
    
    const dialogRef = this.dialog.open(LocationServiceBulkAddDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        locationId: this.locationId,
        availableConcepts: this.availableConcepts,
        availableStaff: this.availableStaff,
        existingConceptIds: existingConceptIds,
        conceptStatuses: this.conceptStatuses
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadConcepts();
      }
    });
  }

  confirmDelete(concept: LocationConceptAdminDto): void {
    if (!this.locationId) {
      console.error('No se puede eliminar: locationId no disponible');
      this.errorMessage = 'No se puede eliminar. ID del centro de trabajo no disponible.';
      return;
    }

    const dialogRef = this.dialog.open(LocationServiceDeleteDialogComponent, {
      width: '500px',
      maxWidth: '96vw',
      data: {
        conceptTitle: concept.title
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.errorMessage = null;

        this.conceptService.deleteLocationConcept(
          this.locationId!,
          concept.merchantLocationConceptId
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          response => {
            this.isLoading = false;
            if (response &&  response.isSuccess) {
              this.loadConcepts();
            } else {
              this.errorMessage = response.error ? this.getErrorMessage(response.error) : 'Error desconocido al eliminar';
            }
          },
          error => {
            this.isLoading = false;
            console.error('Error deleting concept:', error);
            this.errorMessage = 'Error al eliminar el servicio. Por favor, intente nuevamente.';
          }
        );
      }
    });
  }

  onConceptDrop(event: CdkDragDrop<LocationConceptAdminDto[]>): void {
    if (!this.locationId || event.previousIndex === event.currentIndex) {
      return;
    }

    const previous = this.concepts.slice();
    moveItemInArray(this.concepts, event.previousIndex, event.currentIndex);

    const items = this.concepts.map((concept, index) => ({
      merchantLocationConceptId: concept.merchantLocationConceptId,
      order: index
    }));

    this.concepts = this.concepts.map((concept, index) => ({ ...concept, order: index }));

    this.conceptService
      .reorderLocationConcepts(this.locationId, items)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response && response.isSuccess) {
            this.showSuccess('Orden guardado correctamente');
          } else {
            this.concepts = previous;
            const message = response && response.error
              ? this.getErrorMessage(response.error)
              : 'No se pudo guardar el nuevo orden.';
            this.errorMessage = message;
            this.showError(message);
          }
        },
        error: err => {
          console.error('Error reordering concepts:', err);
          this.concepts = previous;
          const message = 'No se pudo guardar el nuevo orden. Inténtalo de nuevo.';
          this.errorMessage = message;
          this.showError(message);
        }
      });
  }

  private showSuccess(msg: string): void {
    this.snackBar.open(msg, 'OK', {
      duration: 3000,
      panelClass: ['snack-success'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private showError(msg: string): void {
    this.snackBar.open(msg, 'Cerrar', {
      duration: 5000,
      panelClass: ['snack-error'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private getErrorMessage(error: any): string {
    if (error && error.messages && Array.isArray(error.messages)) {
      return error.messages.join(', ');
    }
    return 'Ha ocurrido un error desconocido';
  }

  clearError(): void {
    this.errorMessage = null;
  }
  public trackByIndex(i: number) { return i; }

}

